import {Component, EventEmitter, Input, OnInit, Output, ElementRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../common/service/lookup.service';
import {Surveyor, EligibleSurveyor, Firm} from './surveyor-details.model';
import {NlrTypeheadDirective} from '../../common/directive/nlr-typehead.directive';
import {Observable, OperatorFunction, debounceTime, distinctUntilChanged, map} from 'rxjs';
import {PlanModel} from '../../common/service/plan-model.model';
import {AuthenticatorService} from '../../common/service/authenticator.service';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {isValidNumber} from '../../common/service/data-util';

@Component({
    selector: 'nlr-plan-surveyor-details',
    imports: [CommonModule, FormsModule, NlrTypeheadDirective, NlrFormFieldHasErrorPipe],
    templateUrl: './surveyor-details.component.html',
    styleUrl: './surveyor-details.component.scss'
})
export class SurveyorDetailsComponent implements OnInit {
    surveyors: Surveyor[];
    eligibleSurveyors = undefined;
    firms: Firm[];
    firm: Firm;
    selectedSurveyorName = undefined;
    selectedAllocatedSurveyorName = undefined;
    submitted = false;
    isCertifyingSurveyorValid = true;
    isAllocatedSurveyorValid = true;

    @Input()
    model: PlanModel;

    @Output()
    modelChanged = new EventEmitter<PlanModel>();

    @Output() validationChanged = new EventEmitter<boolean>();

    surveyorNameSearch: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term =>
                this.getSurveyorNameSearch(term)
                    .filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
                    .slice(0, 10)
            )
        );

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private authenticatorService: AuthenticatorService,
        private elememRef: ElementRef
    ) {}

    ngOnInit(): void {
        this.getAllocatedSurveyors();
        this.getEligibleSurveyors();
        this.getFirms();
    }

    preAllocatedSurveyorNameSearch: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term =>
                this.getAllocatedSurveyorNameSearch(term)
                    .filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
                    .slice(0, 10)
            )
        );

    getSurveyorNameSearch(viewValue: string) {
        this.isCertifyingSurveyorValid = false;
        const EligibleSurveyorsByFullName = this.filterSurveyorsByFullName(this.eligibleSurveyors, viewValue);
        let surveyorNameSearchList = [];

        EligibleSurveyorsByFullName.map(surveyor => {
            surveyorNameSearchList.push(surveyor.familyName + ' ' + surveyor.givenNames);
        });
        return surveyorNameSearchList;
    }

    getAllocatedSurveyorNameSearch(viewValue: string) {
        this.isAllocatedSurveyorValid = false;
        const AllocatedSurveyorsByFullName = this.filterSurveyorsByFullName(this.surveyors, viewValue);
        let allocatedSurveyorNameSearchList = [];

        AllocatedSurveyorsByFullName.map(surveyor => {
            allocatedSurveyorNameSearchList.push(surveyor.familyName + ' ' + surveyor.givenNames);
        });
        return allocatedSurveyorNameSearchList;
    }

    getAllocatedSurveyors() {
        const subs = this.lookupService.retrieveLookup<Surveyor[]>('/surveyors/allocated').subscribe({
            next: data => {
                this.surveyors = data;
                console.log('Surveyor load success ');
            },
            error: error => {
                console.error('Surveyor Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    getEligibleSurveyors() {
        const subs = this.lookupService.retrieveLookup<EligibleSurveyor[]>('/surveyors/eligible').subscribe({
            next: data => {
                this.eligibleSurveyors = data;
                console.log('EligibleSurveyors load success ');
            },
            error: error => {
                console.error('EligibleSurveyors Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    getFirms() {
        const subs = this.lookupService.retrieveLookup<Firm[]>('/surveyingFirms').subscribe({
            next: data => {
                this.firms = data;
                if (!this.model.firmNumber) {
                    const userFirms = this.firms.filter(row => {
                        return row.firmNumber == this.authenticatorService.getUsersFirmNumber();
                    });

                    if (userFirms.length) {
                        this.firm = userFirms[0];
                        this.model.firmNumber = this.firm.firmNumber;
                    }
                }
                console.log('SurveyingFirms load success ');
            },
            error: error => {
                console.error('SurveyingFirms Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    onSurveyFirmChange(newValue: string) {
        this.model.firm['name'] = newValue;
    }

    filterSurveyorsByFullName(surveyors: any, fullName: string) {
        if (this.surveyors === undefined) {
            return [];
        }
        const viewValueLowerCase = fullName.toLowerCase();

        return surveyors
            .filter(surveyor => {
                const surveyorFullNameLowerCase = this.surveyorFullName(surveyor).toLowerCase();
                return surveyorFullNameLowerCase !== viewValueLowerCase;
            })
            .sort((s1, s2) => {
                var s1FullNameLowerCase = this.surveyorFullName(s1).toLowerCase();
                var s2FullNameLowerCase = this.surveyorFullName(s2).toLowerCase();
                if (s1FullNameLowerCase.indexOf(viewValueLowerCase) === 0 && s2FullNameLowerCase.indexOf(viewValueLowerCase) === 0) {
                    return s1FullNameLowerCase.localeCompare(s2FullNameLowerCase);
                } else if (s1FullNameLowerCase.indexOf(viewValueLowerCase) === 0) {
                    return -1;
                } else if (s2FullNameLowerCase.indexOf(viewValueLowerCase) === 0) {
                    return 1;
                } else {
                    return s1FullNameLowerCase.localeCompare(s2FullNameLowerCase);
                }
            });
    }

    formatCertifyingSurveyor() {
        return this.model.certifyingSurveyorIdNumber
            ? this.eligibleSurveyors
                  .filter((surveyor: Surveyor) => {
                      return surveyor.surveyorIdNumber === this.model.certifyingSurveyorIdNumber;
                  })
                  .map((surveyor: Surveyor) => {
                      return this.surveyorFullName(surveyor);
                  })[0] || ''
            : '';
    }

    surveyorFullName(surveyor: EligibleSurveyor | Surveyor) {
        return surveyor.familyName + ' ' + surveyor.givenNames;
    }

    onSurveyorNameChange() {
        const selectedEligibleSurveyor = this.eligibleSurveyors.filter(surveyor => {
            return this.surveyorFullName(surveyor) === this.selectedSurveyorName;
        })[0];
        if (selectedEligibleSurveyor) {
            this.model.certifyingSurveyorIdNumber = selectedEligibleSurveyor.surveyorIdNumber;
            this.isCertifyingSurveyorValid = true;
            this.validationChanged.emit(isValidNumber(this.model.certifyingSurveyorIdNumber));
        }
    }

    onAllocatedSurveyorNameChange() {
        const selectedSurveyor = this.surveyors.filter(surveyor => {
            return this.surveyorFullName(surveyor) === this.selectedAllocatedSurveyorName;
        })[0];
        if (selectedSurveyor) {
            this.model.surveyorIdNumber = selectedSurveyor.surveyorIdNumber;
            this.isAllocatedSurveyorValid = true;
        }
    }

    isFirmNameValid() {
        if (this.firm) {
            return this.firm.firmName === undefined || this.firm.firmName === '' ? false : true;
        }
        return false;
    }
}
